import Alert from '@risk-smart/themed-cloudscape-components/alert';
import { useAmplitude } from '@risksmart-app/components/src/segment';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import type { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ModalBodyWrapper } from 'src/components/form/form/ModalBodyWrapper';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { useWizardStore } from 'src/components/wizard/store/useWizardStore';
import { useGetRiskAssessmentResultById } from 'src/hooks/queries/assessment-result/useGetRiskAssessmentResultById';
import { useGetRiskListOnlyOptimized } from 'src/hooks/queries/risk/useGetRiskListOnlyOptimized';
import { mapControlTypeToRatingType } from 'src/ratings/controlTypeMapper';
import { useScoringSettings } from 'src/ratings/useScoringSettings';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import {
  useInsertRiskAssessmentResult,
  useUpdateRiskAssessmentResult,
} from '@/hooks/mutations/risk-assessment-result';
import { assessmentResultsUrl } from '@/utils/urls';

import RiskAssessmentResultForm from './RiskAssessmentResultForm';
import type {
  MultiImpactRiskAssessmentResultFormDataFields,
  RiskAssessmentResultFormDataFields,
} from './riskAssessmentResultSchema';
import { defaultValues } from './riskAssessmentResultSchema';

type SaveFormData =
  | RiskAssessmentResultFormDataFields
  | MultiImpactRiskAssessmentResultFormDataFields;

const isMultiImpactForm = (
  values: SaveFormData
): values is MultiImpactRiskAssessmentResultFormDataFields =>
  'ImpactRatings' in values;

type CommonFields = Omit<RiskAssessmentResultFormDataFields, 'Impact'>;

const buildFormFields = (
  common: CommonFields,
  impact: number | null | undefined
): RiskAssessmentResultFormDataFields => ({
  ...common,
  Impact: impact,
});

const buildMultiImpactFormFields = (
  common: CommonFields,
  impactCategories: { name: string }[],
  storedImpacts: { Label: string; Value: number }[]
): MultiImpactRiskAssessmentResultFormDataFields => ({
  ...common,
  ImpactRatings: impactCategories.map((c) => ({
    categoryName: c.name,
    rating: storedImpacts.find((i) => i.Label === c.name)?.Value ?? null,
  })),
});

type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  isModalForm: boolean;
  parentId?: string;
  assessedItem?: ObjectWithContributors & { Tier?: number };
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  showAssessmentSelector?: boolean;
  riskIds?: string[];
  header?: string;
};

const ConnectedRiskAssessmentResultForm: FC<Props> = ({
  readonly,
  parentId,
  assessedItem,
  id,
  onDismiss,
  beforeFieldsSlot,
  showAssessmentSelector,
  navigateToResults,
  isModalForm,
  riskIds,
  header,
}) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const { updateFiles } = useMultiParentFileUpdate();
  const [toolsContent, _] = useTools();
  const amplitude = useAmplitude();
  const {
    assessmentId: wizardAssessmentId,
    steps,
    currentStep,
  } = useWizardStore();

  const { data } = useGetRiskAssessmentResultById({
    queryArgs: { id: id! },
    shouldSkip: !id,
  });
  const riskAssessmentResult = data?.risk_assessment_result?.[0];
  const {
    hasPermission: canUpdateRiskAssessmentResult,
    loading: isLoadingUpdateRiskAssessmentResult,
  } = useHasPermissionQuery('update:risk_assessment_result');

  const {
    hasMultipleImpacts,
    impactCategories,
    latestConfigId,
    loading: scoringSettingsLoading,
  } = useScoringSettings({
    configType: 'risk_assessment',
  });

  // Treat the rating as stale while scoring settings are still loading so the
  // form opens read-only and we never race the backend's integrity check.
  const hasStaleConfig =
    !!riskAssessmentResult?.ConfigId &&
    (scoringSettingsLoading ||
      (!!latestConfigId && riskAssessmentResult.ConfigId !== latestConfigId));

  const { insertRiskAssessmentResult } = useInsertRiskAssessmentResult();
  const { updateRiskAssessmentResult } = useUpdateRiskAssessmentResult();
  // Only needed on insert to look up Tier for each selected risk. On update the
  // tier comes from the rating's own parent risk or the assessedItem.
  const { data: riskListData } = useGetRiskListOnlyOptimized({
    queryArgs: {},
    shouldSkip: !!id,
  });
  const riskTierById = useMemo(
    () => new Map((riskListData?.risk ?? []).map((r) => [r.Id, r.Tier])),
    [riskListData?.risk]
  );
  riskIds = riskIds ?? [];
  const parentRisk = riskAssessmentResult?.parents.find((p) => p.risk);
  if (parentRisk?.risk?.Id) {
    riskIds.push(parentRisk.risk.Id);
  } else if (assessedItem?.Id) {
    riskIds.push(assessedItem.Id);
  }

  const onSave = async (values: SaveFormData) => {
    const mode = id ? 'update' : 'new';
    const ratingType = mapControlTypeToRatingType(values.ControlType);
    // On update there is always exactly one rating; on insert N risks → N ratings.
    // Use `[undefined]` as a fallback when we can't resolve a risk id so that one
    // event is still emitted per save attempt (rating_id will still be present).
    const selectedRiskIds: (string | undefined)[] =
      mode === 'update'
        ? [riskIds[0]]
        : (values?.RiskIds?.map((r) => r.value) ?? []);
    const defaultTier = parentRisk?.risk?.Tier ?? assessedItem?.Tier;
    const resolveTier = (riskId: string | undefined) =>
      riskId ? (riskTierById.get(riskId) ?? defaultTier) : defaultTier;

    try {
      const multiImpact = isMultiImpactForm(values);
      const impacts = multiImpact
        ? values.ImpactRatings.filter(
            (r): r is { categoryName: string; rating: number } =>
              r.rating != null
          ).map((r) => ({ Label: r.categoryName, Value: r.rating }))
        : undefined;

      const commonFields = {
        Rating: values.Rating,
        Likelihood: values.Likelihood,
        ControlType: values.ControlType,
        Rationale: values.Rationale,
        TestDate: values.TestDate,
        CustomAttributeData: values.CustomAttributeData ?? null,
        ...(multiImpact
          ? { Impacts: impacts, Impact: undefined }
          : { Impact: values.Impact, Impacts: undefined }),
      };

      const riskAssessmentResultIds: string[] = [];
      if (riskAssessmentResult) {
        const result = await updateRiskAssessmentResult({
          ...commonFields,
          Id: riskAssessmentResult.Id,
          AssessmentId: values.AssessmentId,
          OriginalTimestamp: riskAssessmentResult.ModifiedAtTimestamp ?? '',
          ParentRiskId: riskIds[0] ?? null,
        });
        if (result.updateChildRiskAssessmentResult?.affected_rows === 0) {
          throw new Error('Risk assessment result update failed');
        }
        riskAssessmentResultIds.push(id!);
      } else {
        const result = await insertRiskAssessmentResult({
          ...commonFields,
          AssessmentId: parentId ?? values.AssessmentId,
          RiskIds: values?.RiskIds?.map((r) => r.value) ?? [],
        });

        if (!result.insertChildRiskAssessmentResult?.Ids) {
          throw new Error('Risk assessment result id is missing');
        }
        riskAssessmentResultIds.push(
          ...result.insertChildRiskAssessmentResult.Ids
        );
      }

      await updateFiles({
        parentIds: riskAssessmentResultIds,
        parentType: Parent_Type_Enum.RiskAssessmentResult,
        selectedFiles: values.files,
        originalFiles: riskAssessmentResult?.files.map((f) => f.file) ?? [],
      });

      selectedRiskIds.forEach((selectedRiskId, i) => {
        const tier = resolveTier(selectedRiskId);
        amplitude.logEvent('Risk Rating Saved', {
          mode,
          ...(selectedRiskId && { risk_id: selectedRiskId }),
          ...(tier !== undefined && { risk_tier: tier }),
          rating_id: riskAssessmentResultIds[i],
          ...(ratingType && { rating_type: ratingType }),
        });
      });

      if (navigateToResults && parentId) {
        navigate(assessmentResultsUrl(parentId));
      }
    } catch (error) {
      // Emit one 'Save Failed' event per rating the user attempted to save,
      // matching the per-rating shape of 'Risk Rating Saved'. On insert no
      // rating IDs exist yet, so rating_id is omitted for inserts.
      const failureRiskIds =
        selectedRiskIds.length > 0 ? selectedRiskIds : [undefined];
      failureRiskIds.forEach((selectedRiskId) => {
        const tier = resolveTier(selectedRiskId);
        amplitude.logEvent('Risk Rating Save Failed', {
          mode,
          ...(selectedRiskId && { risk_id: selectedRiskId }),
          ...(tier !== undefined && { risk_tier: tier }),
          ...(mode === 'update' && id && { rating_id: id }),
          ...(ratingType && { rating_type: ratingType }),
        });
      });
      throw error;
    }
  };

  const commonFields = riskAssessmentResult
    ? {
        Rating: riskAssessmentResult.Rating,
        Rationale: riskAssessmentResult.Rationale,
        Likelihood: riskAssessmentResult.Likelihood,
        ControlType: riskAssessmentResult.ControlType,
        AssessmentId:
          riskAssessmentResult.parents.find((p) => p.assessment)?.assessment
            ?.Id ?? null,
        RiskIds: riskIds.map((c) => ({ value: c })),
        TestDate: riskAssessmentResult.TestDate,
        CustomAttributeData: riskAssessmentResult.CustomAttributeData,
        files: riskAssessmentResult?.files.map((f) => f.file) ?? [],
      }
    : undefined;

  const isMultiImpactEdit =
    hasMultipleImpacts && riskAssessmentResult?.impacts?.length;

  const value = commonFields
    ? isMultiImpactEdit
      ? buildMultiImpactFormFields(
          commonFields,
          impactCategories,
          riskAssessmentResult.impacts
        )
      : buildFormFields(commonFields, riskAssessmentResult!.Impact)
    : undefined;

  return (
    <RiskAssessmentResultForm
      header={header}
      defaultValues={{
        ...defaultValues,
        AssessmentId: toolsContent === 'wizard' ? wizardAssessmentId : parentId,
        RiskIds: riskIds.map((c) => ({ value: c })),
        ControlType:
          (steps[currentStep]
            ?.controlType as Risk_Assessment_Result_Control_Type_Enum) ||
          defaultValues.ControlType,
      }}
      values={value}
      onSave={onSave}
      readOnly={
        hasStaleConfig ||
        (readonly &&
          !canUpdateRiskAssessmentResult &&
          !isLoadingUpdateRiskAssessmentResult)
      }
      onDismiss={onDismiss}
      renderTemplate={(renderProps) =>
        isModalForm ? (
          <ModalBodyWrapper {...renderProps} />
        ) : (
          <PageWrapper {...renderProps} />
        )
      }
      disableRiskSelector={id != undefined}
      beforeFieldsSlot={
        <>
          {hasStaleConfig && (
            <Alert
              data-testid={'stale-config-alert'}
              statusIconAriaLabel={'Warning'}
              type={'warning'}
            >
              {t('ratings.stale_config_alert')}
            </Alert>
          )}
          {beforeFieldsSlot}
        </>
      }
      showSelector={showAssessmentSelector ? 'rating' : undefined}
      assessmentMode={'rating'}
      configType={'risk_assessment'}
    />
  );
};

export default ConnectedRiskAssessmentResultForm;
